/**
 * MainLayout Component
 *
 * The main application layout that wraps all pages.
 * Includes AppBar, FlashAlert, loading screen, and content area.
 *
 * Features:
 * - AppBar with navigation menu and user avatar
 * - FlashAlert for notifications
 * - Loading screen during authentication
 * - Timeout handling for slow loads
 * - Responsive layout
 *
 * Ported from Vue App.vue layout structure
 *
 * @example
 * ```tsx
 * <MainLayout>
 *   <BookingPage />
 * </MainLayout>
 * ```
 */

import logoImage from "@/assets/brilliantlogotransparentnegative.png";
import { useAuth } from "@/hooks/auth/useAuth";
import { useUser } from "@/hooks/auth/useUser";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppBar } from "@/components/ui/navigation/AppBar";
import type { NavMenuItem } from "@/components/ui/navigation/AppBar/types";
import { cx } from "@/utils/cx";
import styles from "./MainLayout.module.css";
import type { MainLayoutProps } from "./types";

// Timeout for showing loading timeout message (30 seconds)
const LOADING_TIMEOUT_MS = 30_000;

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  className = "",
  "data-testid": testId,
}) => {
  const navigate = useNavigate();

  // Get MSAL state directly to track interaction status
  const { inProgress, accounts } = useMsal();

  const { initialize, logout, isLoading, error } = useAuth();
  const { isAuthenticated, photo, displayName, email, pagesAccessible } =
    useUser();

  const [loadTimedOut, setLoadTimedOut] = useState(false);
  const initializingRef = useRef(false);

  // Initialize auth when MSAL finishes handling redirect
  useEffect(() => {
    // Only try to initialize when MSAL is not in the middle of an interaction
    if (inProgress === InteractionStatus.None && !initializingRef.current) {
      initializingRef.current = true;
      initialize().finally(() => {
        initializingRef.current = false;
      });
    }
  }, [inProgress, accounts.length, initialize]);

  // Set timeout for slow loading
  useEffect(() => {
    if (isLoading && !loadTimedOut) {
      const timeoutId = window.setTimeout(() => {
        setLoadTimedOut(true);
      }, LOADING_TIMEOUT_MS);

      return () => clearTimeout(timeoutId);
    }
  }, [isLoading, loadTimedOut]);

  // Build navigation menu items from accessible pages
  const menuItems: NavMenuItem[] = pagesAccessible.map((page) => ({
    label: page.name,
    route: page.route,
    icon: getIconForRoute(page.route),
    testId: `nav-${page.name.toLowerCase().replace(/\s+/g, "-")}`,
  }));

  // Handle navigation
  const handleNavigate = useCallback(
    (route: string) => {
      navigate(route);
    },
    [navigate],
  );

  // Handle logout
  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  // Handle ACCESS_DENIED error - redirect to forbidden page
  useEffect(() => {
    if (error?.message === "ACCESS_DENIED") {
      navigate("/forbidden", { replace: true });
    }
  }, [error, navigate]);

  // Determine if we should show the loading screen
  // Show loading when: MSAL is handling interaction, or we're loading, or not authenticated
  // Don't show loading if we have an ACCESS_DENIED error (will redirect to forbidden)
  const msalBusy = inProgress !== InteractionStatus.None;
  const hasAccessDenied = error?.message === "ACCESS_DENIED";
  const showLoading =
    (msalBusy || isLoading || !isAuthenticated) && !hasAccessDenied;
  const ready = !showLoading;

  // Build class names
  const appClasses = cx(styles.app, className);
  const loadingClasses = cx(styles.loadingContainer, ready && styles.hide);

  return (
    <div className={appClasses} data-testid={testId || "app-root"}>
      {/* AppBar - only show when ready */}
      {ready && (
        <AppBar
          menuItems={menuItems}
          userAvatarSrc={photo}
          userName={displayName}
          userEmail={email}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
          data-testid="app-bar"
        />
      )}

      {/* Main content area */}
      <main className={styles.main}>{ready && children}</main>

      {/* Loading screen overlay */}
      <div className={loadingClasses} aria-hidden={ready}>
        <img
          src={logoImage}
          alt="Brilliant Logo"
          className={styles.logoImage}
          fetchPriority="high"
        />

        {/* Spinner - hide when timed out */}
        {!loadTimedOut && (
          <div className={styles.loader}>
            <div className={styles.spinner} aria-label="Loading..." />
          </div>
        )}

        {/* Timeout message */}
        {loadTimedOut && (
          <p
            className={`${styles.timeoutMessage} ${error ? styles.errorMessage : ""}`}
          >
            {error ? (
              <span>
                Something went wrong while signing you in. Check your connection
                and refresh to try again.
              </span>
            ) : (
              <span>
                This is taking a little longer than usual. Please wait a moment
                or refresh the page.
              </span>
            )}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Get icon name for a given route
 */
function getIconForRoute(route: string): string {
  const iconMap: Record<string, string> = {
    "/booking": "calendar",
    "/office": "menu", // Using menu as placeholder for floor-plan
    "/admin": "settings",
    "/superadmin": "settings",
  };
  return iconMap[route] || "chevronRight";
}

MainLayout.displayName = "MainLayout";
